import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Popover } from '@mui/material';

import { DATA_GRID_FILTER_PANEL_TYPE } from 'constants/components';
import { menuStyles } from 'components/simple';
import { FilterIcon, FilterSelectedIcon } from 'assets/icons';

import { FilterPanel } from './FilterPanel';

const filterMenuPaper = {
  marginBlock: 1,
  maxWidth: 320,
  maxHeight: 400,
  overflow: 'auto',

  '.MuiListItemText-root': {
    minWidth: 0,
  },

  '.MuiListItemText-primary': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
};

const styles = {
  menuPaper: [menuStyles.panel, filterMenuPaper],
  multiSelectMenuPaper: [menuStyles.panel, filterMenuPaper, { paddingBottom: 1.5 }],
};

export const ColumnHeaderFilterButton = ({
  field,
  headerName = '',
  filterType = undefined,
  filterValue = null,
  onFilterModelChange,
  valueOptions,
  withTransparentHeader,
}) => {
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);

  const isOpenMenu = Boolean(filterMenuAnchorEl);

  const handleOpenFilterMenu = (event) => {
    event.stopPropagation();
    setFilterMenuAnchorEl(event.currentTarget);
  };

  const handleCloseFilterMenu = (event) => {
    event.stopPropagation();
    setFilterMenuAnchorEl(null);
  };

  const onChangeFilterModel = (updatedValue) => {
    if (typeof onFilterModelChange === 'function') {
      onFilterModelChange(field, updatedValue);
    }

    setFilterMenuAnchorEl(null);
  };

  return (
    <>
      <IconButton
        id={`${field}-menu-button`}
        tabIndex={-1}
        aria-label={`Filter ${headerName || field} column`}
        aria-haspopup="true"
        aria-expanded={isOpenMenu || undefined}
        aria-controls={isOpenMenu ? `${field}-filter-menu` : undefined}
        onClick={handleOpenFilterMenu}
        size="small"
      >
        <Box
          component={filterValue?.length ? FilterSelectedIcon : FilterIcon}
          width={16}
          height={16}
          color={withTransparentHeader ? 'text.primary' : 'text.secondary'}
        />
      </IconButton>

      <Popover
        disableAutoFocus
        id={`${field}-filter-menu`}
        anchorEl={filterMenuAnchorEl}
        open={isOpenMenu}
        onClose={handleCloseFilterMenu}
        aria-labelledby={`${field}-menu-button`}
        slotProps={{
          paper: {
            sx:
              filterType === DATA_GRID_FILTER_PANEL_TYPE.MULTIPLE_SELECT
                ? styles.multiSelectMenuPaper
                : styles.menuPaper,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <FilterPanel
          filterType={filterType}
          filterProps={{
            filterValue,
            valueOptions,
            onChangeFilterModel,
          }}
        />
      </Popover>
    </>
  );
};

ColumnHeaderFilterButton.propTypes = {
  field: PropTypes.string.isRequired,
  headerName: PropTypes.string,
  filterType: PropTypes.oneOf(Object.values(DATA_GRID_FILTER_PANEL_TYPE)),
  valueOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  filterValue: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onFilterModelChange: PropTypes.func.isRequired,
  withTransparentHeader: PropTypes.bool.isRequired,
};
